import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useDispatch, useSelector } from "@src/store";
import {
  getAtlasBuildingRuleId,
  getIsAtlasCreationSessionActive,
  setAiChat,
} from "@src/store/slices/global";

import { useRuleSelectionGraph } from "../graph/useRuleSelectionGraph";
import { resolveAtlasChatPresentation } from "./atlasChatPresentation";
import { type AgenticLogicSurface, resolveAgenticLogicView } from "./surface";

/**
 * Publishes how the Atlas chat is framed on this surface (see
 * `resolveAtlasChatPresentation`) for the host that renders it — the layout
 * shell, or a takeover dialog — which lives outside this page tree.
 *
 * The graph is read only for the rule the session is building: that is the
 * one rule whose first graph flips the layout, and it shares its query keys
 * with the canvas, which is already polling for it.
 */
export const useAtlasChatPresentation = (surface: AgenticLogicSurface) => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const view = resolveAgenticLogicView(search, surface.ruleParam);
  const isSessionActive = useSelector(getIsAtlasCreationSessionActive);
  const buildingRuleId = useSelector(getAtlasBuildingRuleId);
  const buildingRuleOnScreen =
    view.kind === "rule" && view.ruleId === buildingRuleId
      ? buildingRuleId
      : undefined;
  const { graph } = useRuleSelectionGraph(buildingRuleOnScreen);
  const presentation = resolveAtlasChatPresentation({
    view,
    isSessionActive,
    buildingRuleId,
    hasGraph: graph !== null,
  });

  useEffect(() => {
    dispatch(setAiChat({ key: "presentation", value: presentation }));
  }, [dispatch, presentation]);

  // Leaving the surface hands the chat back its rail: no other page knows to.
  useEffect(
    () => () => {
      dispatch(setAiChat({ key: "presentation", value: "rail" }));
    },
    [dispatch]
  );
};
